import { useContext, useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { Button } from '@/common/components/ui/button';
import { SelectItem } from '@/common/components/ui/select';
import { Sidebar } from '@/common/components/sidebar/Sidebar';
import { SidebarContext } from '@/common/components/sidebar/SidebarContext';
import { ParameterField } from '@/features/devices/components/parameter-field/ParameterField';
import { DeviceService } from '@/features/devices/services/DeviceService';
import { deviceQueryKeys } from '@/features/devices/queries';
import { PARENT_NONE, makeDeviceFormSchema } from '@/features/devices/config';
import { useDeviceParameters } from '@/features/devices/hooks/useDeviceParameters';
import type { Device, DeviceType } from '@/features/devices/services/schemas';

interface AddDeviceSidebarProps {
  supplyPointId: number;
  devices: Device[];
  deviceTypes: DeviceType[];
  preselectedParentId?: number | null;
}

export function AddDeviceSidebar({
  supplyPointId,
  devices,
  deviceTypes,
  preselectedParentId = null,
}: AddDeviceSidebarProps) {
  const queryClient = useQueryClient();
  const { onClose } = useContext(SidebarContext);

  const [createdDeviceId, setCreatedDeviceId] = useState<number | null>(null);
  const [createdTypeId, setCreatedTypeId] = useState<number | null>(null);

  const parameterDefinitions =
    deviceTypes
      .find((t) => t.id === createdTypeId)
      ?.parameterdefinitionSet.filter((d) => d.role === 'parameter') ?? [];

  const params = useDeviceParameters(parameterDefinitions, {
    deviceId: createdDeviceId,
    onSuccess: () => onClose?.(),
  });

  // When preselectedParentId is set, filter device types to only child types of the parent's group
  const preselectedParentDevice = devices.find((d) => d.id === preselectedParentId);
  const preselectedParentType = deviceTypes.find(
    (t) => t.id === preselectedParentDevice?.deviceType
  );
  const preselectedParentGroupId = preselectedParentType?.deviceGroup.id ?? null;

  const visibleDeviceTypes =
    preselectedParentGroupId !== null
      ? deviceTypes.filter((t) => t.deviceGroup.parent === preselectedParentGroupId)
      : deviceTypes;

  const formSchema = useMemo(() => makeDeviceFormSchema(deviceTypes), [deviceTypes]);

  const form = useAppForm(
    appFormOptions({
      schema: formSchema,
      defaultValues: {
        name: null,
        deviceType: null,
        parent: preselectedParentId !== null ? String(preselectedParentId) : PARENT_NONE,
      },
      onSubmit: async ({ value }) => {
        const selectedType = deviceTypes.find((t) => t.id === value.deviceType);
        const requiresParent =
          preselectedParentId !== null || (selectedType?.deviceGroup.parent ?? null) !== null;

        const newDevice = await DeviceService.create({
          name: value.name,
          deviceType: value.deviceType,
          energySystem: supplyPointId,
          parent: requiresParent ? value.parent : null,
        });

        await queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });

        const definitions =
          deviceTypes
            .find((t) => t.id === value.deviceType)
            ?.parameterdefinitionSet.filter((d) => d.role === 'parameter') ?? [];

        if (definitions.length === 0) {
          onClose?.();
          return;
        }

        const defaults = Object.fromEntries(definitions.map((def) => [def.id, def.value ?? '']));
        params.reset(defaults);
        setCreatedTypeId(value.deviceType);
        setCreatedDeviceId(newDevice.id);
      },
    })
  );

  // Group-aware parent logic (only used when preselectedParentId is NOT set)
  const selectedDeviceTypeId = useStore(form.store, (s) => s.values.deviceType);
  const selectedDeviceType = deviceTypes.find((t) => t.id === Number(selectedDeviceTypeId));
  const parentGroupId = selectedDeviceType?.deviceGroup.parent ?? null;
  const isParentRequired = preselectedParentId === null && parentGroupId !== null;

  const parentGroupTypeIds =
    parentGroupId !== null
      ? deviceTypes.filter((t) => t.deviceGroup.id === parentGroupId).map((t) => t.id)
      : [];
  const availableParents =
    parentGroupId !== null ? devices.filter((d) => parentGroupTypeIds.includes(d.deviceType)) : [];

  const isStep2 = createdDeviceId !== null;

  return (
    <Sidebar width={480}>
      <Sidebar.Header className="px-6 pt-6 pb-5 border-b border-border shrink-0">
        <Sidebar.Title>
          {isStep2 ? (
            <Trans>Configure parameters</Trans>
          ) : preselectedParentId !== null ? (
            <Trans>Add child device</Trans>
          ) : (
            <Trans>Add device</Trans>
          )}
        </Sidebar.Title>
        <Sidebar.Description>
          {isStep2 ? (
            <Trans>Set the parameters for this device.</Trans>
          ) : preselectedParentId !== null ? (
            (() => {
              const parentName = devices.find((d) => d.id === preselectedParentId)?.name ?? '';
              return <Trans>Parent: {parentName}</Trans>;
            })()
          ) : (
            <Trans>Configure the device and its connection.</Trans>
          )}
        </Sidebar.Description>
      </Sidebar.Header>

      <Sidebar.Content>
        {!isStep2 ? (
          <form.AppForm>
            <form.FormElement>
              <div className="space-y-5">
                <FormErrors />

                <form.AppField name="name">
                  {(field) => (
                    <field.TextField
                      label={<Trans>Device name</Trans>}
                      placeholder={t`e.g. Solar Inverter A`}
                      required
                    />
                  )}
                </form.AppField>

                <form.AppField name="deviceType">
                  {(field) => (
                    <field.SelectField
                      label={<Trans>Device type</Trans>}
                      required
                      placeholder={t`Select type`}
                    >
                      {visibleDeviceTypes.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </field.SelectField>
                  )}
                </form.AppField>

                {isParentRequired && (
                  <div className="flex flex-col gap-1">
                    <form.AppField name="parent">
                      {(field) => (
                        <field.SelectField
                          label={<Trans>Parent device</Trans>}
                          required
                          placeholder={t`Select parent`}
                          disabled={availableParents.length === 0}
                        >
                          {availableParents.map((d) => {
                            const typeName =
                              deviceTypes.find((t) => t.id === d.deviceType)?.name ?? '';
                            return (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.name} ({typeName})
                              </SelectItem>
                            );
                          })}
                        </field.SelectField>
                      )}
                    </form.AppField>
                    {availableParents.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        <Trans>
                          No devices available in the required group. Add a parent device first.
                        </Trans>
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => onClose?.()}>
                    <Trans>Cancel</Trans>
                  </Button>
                  <FormSubmit>
                    <Trans>Create device</Trans>
                  </FormSubmit>
                </div>
              </div>
            </form.FormElement>
          </form.AppForm>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              {parameterDefinitions.map((def) => (
                <ParameterField key={def.id} definition={def} {...params.getFieldProps(def)} />
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => onClose?.()}>
                <Trans>Skip</Trans>
              </Button>
              <Button type="button" onClick={() => void params.save()} disabled={params.isSaving}>
                <Trans>Save & close</Trans>
              </Button>
            </div>
          </div>
        )}
      </Sidebar.Content>
    </Sidebar>
  );
}
